import React, { useState } from 'react';
import { CheckCircle2, Circle, Loader2, ChevronDown, ChevronRight, ClipboardList, Search } from 'lucide-react';
import type { PlanData, PlanStep } from '../helpers/planBlockParser';
import styles from './PlanCard.module.css';

interface PlanCardProps {
  data: PlanData;
  onSelectOption: (questionId: string, option: string) => void;
  onCreateTasks?: () => void;
  answered?: boolean;
}

function StepItem({ step, index }: { step: PlanStep; index: number }) {
  const isDone = step.status === 'done';
  const isActive = step.status === 'active';
  return (
    <div
      className={`${styles.step} ${isDone ? styles.stepDone : ''} ${isActive ? styles.stepActive : ''}`}
    >
      <div className={styles.stepIcon}>
        {isDone ? (
          <CheckCircle2 size={14} />
        ) : isActive ? (
          <Loader2 size={14} className={styles.spin} />
        ) : (
          <Circle size={14} />
        )}
      </div>
      <div className={styles.stepContent}>
        <div className={styles.stepNum}>{index + 1}</div>
        <div className={styles.stepBody}>
          <div className={styles.stepTitle}>{step.title}</div>
          {step.detail && <div className={styles.stepDetail}>{step.detail}</div>}
        </div>
      </div>
    </div>
  );
}

export function PlanCard({ data, onSelectOption, onCreateTasks, answered }: PlanCardProps) {
  const [stepsExpanded, setStepsExpanded] = useState(true);
  const [researchExpanded, setResearchExpanded] = useState(false);
  const isConfirmed = data.status === 'confirmed' || data.status === 'executing';

  return (
    <div className={`${styles.card} ${isConfirmed ? styles.confirmed : ''}`}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          {isConfirmed ? (
            <CheckCircle2 size={16} />
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          )}
        </div>
        <div className={styles.headerText}>
          <div className={styles.planLabel}>{isConfirmed ? 'Plan Confirmed' : 'Plan'}</div>
          <div className={styles.planTitle}>{data.title}</div>
        </div>
        {isConfirmed && <div className={styles.confirmedBadge}>✓ Ready</div>}
      </div>

      {/* Description */}
      {data.description && <div className={styles.description}>{data.description}</div>}

      {/* Research Summary */}
      {data.research_summary && (
        <div className={styles.researchSection}>
          <button
            className={styles.researchToggle}
            onClick={() => setResearchExpanded((v) => !v)}
          >
            {researchExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <Search size={12} />
            <span>Research Findings</span>
          </button>
          {researchExpanded && (
            <div className={styles.researchContent}>{data.research_summary}</div>
          )}
        </div>
      )}

      {/* Steps */}
      {Array.isArray(data.steps) && data.steps.length > 0 && (
        <div className={styles.stepsSection}>
          <button className={styles.stepsToggle} onClick={() => setStepsExpanded((v) => !v)}>
            {stepsExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <span>{data.steps.length} Steps</span>
          </button>
          {stepsExpanded && (
            <div className={styles.stepsList}>
              {data.steps.map((step, i) => (
                <StepItem key={step?.id ?? i} step={step} index={i} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Answers so far */}
      {data.answers && typeof data.answers === 'object' && Object.keys(data.answers).length > 0 && (
        <div className={styles.answersSection}>
          {Object.entries(data.answers).map(([qid, answer]) => (
            <div key={qid} className={styles.answerRow}>
              <CheckCircle2 size={12} className={styles.answerCheck} />
              <span className={styles.answerText}>{String(answer)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Current question */}
      {data.question && data.question.text && !answered && !isConfirmed && (
        <div className={styles.questionSection}>
          <div className={styles.questionText}>{data.question.text}</div>
          <div className={styles.optionsList}>
            {Array.isArray(data.question.options) &&
              data.question.options.map((opt, i) => (
                <button
                  key={i}
                  className={styles.optionBtn}
                  onClick={() => onSelectOption(data.question!.id, opt?.label ?? String(i + 1))}
                >
                  <span className={styles.optionLabel}>{opt?.label}</span>
                  {opt?.description && <span className={styles.optionDesc}>{opt.description}</span>}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Confirmed state — Create Tasks button or execution hint */}
      {isConfirmed && (
        <div className={styles.confirmedActions}>
          {onCreateTasks ? (
            <button className={styles.createTasksBtn} onClick={onCreateTasks}>
              <ClipboardList size={14} />
              Create Tasks from Plan
            </button>
          ) : (
            <div className={styles.executionHint}>
              Plan is confirmed. Tell the AI to begin — e.g. <em>"Let's start building"</em>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
